import React, { useState, useEffect } from 'react';
import CrateUsageCard from './CrateUsageCard';
import StorageUsageCard from './StorageUsageCard';
import ActiveSharesCard from './ActiveSharesCard';
import SecretsCard from './SecretsCard';
import CreateBucketForm from '../CreateBucketForm';
import './HomeView.css';

interface ShareBucket {
  label: string;
  count: number;
}

interface UsageData {
  crateCount: number;
  storageUsedGB: number;
  maxStorageGB: number;
  shareBuckets: ShareBucket[];
  totalShares: number;
  secretsByStatus: Array<{ status: string; count: number }>;
  sharesUsed: number;
  sharesLimit: number;
  secretsUsed: number;
  secretsLimit: number;
}

const HomeView: React.FC<{ isAdmin?: boolean }> = ({ isAdmin }) => {
  const [usageData, setUsageData] = useState<UsageData>({
    crateCount: 0,
    storageUsedGB: 0,
    maxStorageGB: 10,
    shareBuckets: [],
    totalShares: 0,
    secretsByStatus: [],
    sharesUsed: 0,
    sharesLimit: 0,
    secretsUsed: 0,
    secretsLimit: 0,
  });
  useEffect(() => {
    fetchUsageData();
  }, []);

  const fetchUsageData = async () => {
    try {
      const { apiService } = await import('../../services/api');

      const [bucketsResult, subscriptionResult, sharesResult, secretsResult] = await Promise.allSettled([
        apiService.listBuckets(),
        apiService.getSubscription(),
        apiService.listShares(),
        apiService.listOtpShares(),
      ]);

      const bucketsResponse = bucketsResult.status === 'fulfilled' ? bucketsResult.value : null;
      const buckets = bucketsResponse?.buckets || [];
      const crateCount = buckets.length;
      const quota = (bucketsResponse as any)?.quota;
      const storageUsedGB = quota ? quota.usedGB : 0;

      let maxStorageGB = quota ? quota.totalGB : 10;
      let sharesUsed = 0;
      let sharesLimit = 0;
      let secretsUsed = 0;
      let secretsLimit = 0;
      if (subscriptionResult.status === 'fulfilled' && subscriptionResult.value.usage) {
        const subUsage = subscriptionResult.value.usage;
        maxStorageGB = subUsage.storageLimit / (1024 * 1024 * 1024);
        sharesUsed = subUsage.sharesUsed ?? 0;
        sharesLimit = subUsage.sharesLimit ?? 0;
        secretsUsed = subUsage.secretsUsed ?? 0;
        secretsLimit = subUsage.secretsLimit ?? 0;
      }

      let shareBuckets: ShareBucket[] = [];
      let totalShares = 0;
      if (sharesResult.status === 'fulfilled') {
        const shares = sharesResult.value.shares || [];
        totalShares = shares.length;
        const now = Date.now();
        const counts = { d1: 0, d3: 0, d7: 0, d7plus: 0 };
        shares.forEach((share: any) => {
          const ageDays = (now - new Date(share.createdAt).getTime()) / 86400000;
          if (ageDays < 1) counts.d1++;
          else if (ageDays < 3) counts.d3++;
          else if (ageDays < 7) counts.d7++;
          else counts.d7plus++;
        });
        shareBuckets = [
          { label: '1 day', count: counts.d1 },
          { label: '3 days', count: counts.d3 },
          { label: '7 days', count: counts.d7 },
          { label: '7+ days', count: counts.d7plus },
        ];
      }

      let secretsByStatus: Array<{ status: string; count: number }> = [];
      if (secretsResult.status === 'fulfilled') {
        const secrets = secretsResult.value.shares || [];
        const counts: Record<string, number> = {};
        secrets.forEach((s: any) => { counts[s.status] = (counts[s.status] || 0) + 1; });
        secretsByStatus = Object.entries(counts).map(([status, count]) => ({ status, count }));
      }

      setUsageData({ crateCount, storageUsedGB, maxStorageGB, shareBuckets, totalShares, secretsByStatus, sharesUsed, sharesLimit, secretsUsed, secretsLimit });
    } catch (err) {
      console.error('Failed to fetch usage data:', err);
    }
  };

  return (
    <div className="home-view">
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        marginBottom: '2rem' 
      }}>
        <div style={{ 
          width: '100%', 
          maxWidth: '600px' 
        }}>
          <CreateBucketForm
            onBucketCreated={fetchUsageData}
            isAdmin={isAdmin}
          />
        </div>
      </div>
      <h1 className="home-title">Dashboard Overview</h1>
      <div className="visualization-grid">
        <CrateUsageCard crateCount={usageData.crateCount} />
        <StorageUsageCard storageUsedGB={usageData.storageUsedGB} maxStorageGB={usageData.maxStorageGB} />
        <ActiveSharesCard shareBuckets={usageData.shareBuckets} totalShares={usageData.totalShares} sharesUsed={usageData.sharesUsed} sharesLimit={usageData.sharesLimit} />
        <SecretsCard secretsByStatus={usageData.secretsByStatus} secretsUsed={usageData.secretsUsed} secretsLimit={usageData.secretsLimit} />
      </div>
    </div>
  );
};

export default HomeView;
